import React, { useEffect, useState, useRef } from 'react'
import { useDataValue, useDataSubmit, useDataChange } from 'Simple/Data.js'
import { TASK_PRIORITIES_ORDER } from 'Data/constants.js'
import { equals } from 'Data/aggregate.js'
import { ControlledBoard, moveCard } from '@local/react-kanban'
import View from './view.js'
import Card from './Card'
import EmptyCard from './EmptyCard'
import StageHeader from './StageHeader'
import '@local/react-kanban/dist/styles.css'
import './style.css'

let PRIORITY_ORDER = Object.fromEntries(
  Object.values(TASK_PRIORITIES_ORDER).map(({ label, order }) => [label, order])
)

function sortCards(cards, sortSelected) {
  if (!sortSelected || Object.values(sortSelected).every(v => v == null)) {
    return [...cards].sort((a, b) => a.order - b.order)
  }

  let sortField = Object.keys(sortSelected).find(
    key => sortSelected[key] !== null
  )
  let sortDirection = sortSelected[sortField]

  let sorted = [...cards].sort((a, b) => {
    let aValue = a[sortField]
    let bValue = b[sortField]

    if (aValue == null && bValue == null) return 0
    if (aValue == null) return 1
    if (bValue == null) return -1

    if (sortField === 'priority') {
      aValue = PRIORITY_ORDER[aValue] ?? 0
      bValue = PRIORITY_ORDER[bValue] ?? 0
    } else if (sortField === 'due_date' || sortField === 'created_at') {
      aValue = new Date(aValue).getTime()
      bValue = new Date(bValue).getTime()
    }

    if (aValue === bValue) return a.order - b.order
    return aValue < bValue ? -1 : 1
  })

  return sortDirection === 'desc' ? sorted.reverse() : sorted
}

export default function Logic(props) {
  let tabSorting = useDataValue({
    context: 'tab',
    path: 'selected.sorting',
    viewPath: props.viewPath,
  })

  let [stageSorts, setStageSorts] = useState(tabSorting || {})
  let skipSortRef = useRef(false)
  let clearSortFnsRef = useRef({})

  let stages = useDataValue({
    context: 'board',
    path: 'stages',
    viewPath: props.viewPath,
  })

  let tasks = useDataValue({
    context: 'board_tasks',
    path: 'tasks_tasks',
    viewPath: props.viewPath,
  })

  let submit = useDataSubmit({
    context: 'tab',
    viewPath: props.viewPath,
  })

  let changeFilters = useDataChange({
    context: 'filters',
    viewPath: props.viewPath,
  })

  let changeTabSorting = useDataChange({
    context: 'tab',
    path: 'selected.sorting',
    viewPath: props.viewPath,
  })

  let [board, setBoard] = useState({
    columns: [],
  })

  let [movedCardId, setMovedCardId] = useState(null)

  let handleSortChange = (stageId, sortSelected) => {
    setStageSorts(current => ({
      ...current,
      [stageId]: sortSelected,
    }))

    let hasActiveSort =
      sortSelected && Object.values(sortSelected).some(v => v !== null)
    let hadActiveSort =
      tabSorting?.[stageId] &&
      Object.values(tabSorting[stageId]).some(v => v !== null)

    if (
      !equals(tabSorting?.[stageId], sortSelected) &&
      (hasActiveSort || hadActiveSort)
    ) {
      changeTabSorting({ ...(tabSorting || {}), [stageId]: sortSelected })
    }
  }

  let handleRegisterClearSort = (stageId, clearFn) => {
    clearSortFnsRef.current[stageId] = clearFn
  }

  useEffect(() => {
    // Skip rebuilding during manual card moves to preserve optimistic update
    if (skipSortRef.current) {
      skipSortRef.current = false
      return
    }

    // Normalize tasks once, and bucket by stage_id
    let tasksByStageId = (tasks ?? []).reduce((map, t) => {
      let { patients, assignees, ...task } = t

      let normalized = {
        ...task,
        patient: patients?.[0],
        assignee: assignees?.[0],
      }

      let stageId = normalized.stage_id

      if (!map.has(stageId)) map.set(stageId, [])
      map.get(stageId).push(normalized)

      return map
    }, new Map())

    // Build columns from stages and only sort that stage’s cards
    setBoard({
      columns: (stages || []).map(({ id, text: title, subscribers }) => {
        let unsortedCards = tasksByStageId.get(id) || []

        return {
          id,
          title,
          is_current_user_subscribed: (subscribers?.length || 0) > 0,
          cards: sortCards(unsortedCards, stageSorts[id]),
        }
      }),
    })
  }, [stages, tasks, stageSorts])

  let handleCardMove = (card, source, destination) => {
    if (card?.placeholder) return

    let destinationColumn = board.columns.find(
      column => column.id === destination.toColumnId
    )
    if (!destinationColumn) {
      console.error('Destination column not found:', destination.toColumnId)
      return
    }

    // Check if destination column has active sort
    let destinationSort = stageSorts[destination.toColumnId]
    let isSorted =
      destinationSort && Object.values(destinationSort).some(v => v !== null)
    let isSameColumn = source.fromColumnId === destination.toColumnId

    if (isSorted && isSameColumn) {
      // Manual reorder within a sorted column - assign new orders and clear sort
      let updatedCards = [...destinationColumn.cards]
      updatedCards.splice(source.fromPosition, 1)
      updatedCards.splice(destination.toPosition, 0, card)

      let startOrder = Date.now()
      let orderGap = 1000

      let updates = updatedCards
        .filter(c => !c.placeholder)
        .map((c, index) => ({
          task_id: c.id,
          order: startOrder + index * orderGap,
          stage_id: destination.toColumnId,
        }))

      submit({
        type: 'set_bulk_task_order',
        updates,
      })

      let clearFn = clearSortFnsRef.current[destination.toColumnId]
      if (clearFn) {
        clearFn()
      }
    } else {
      // Single card placement: unsorted column, or cross-column move into a sorted column
      let order
      let cards = [...destinationColumn.cards]

      if (isSameColumn) {
        cards.splice(source.fromPosition, 1)
      }

      let prevCard = cards[destination.toPosition - 1]
      let nextCard = cards[destination.toPosition]

      if (prevCard && nextCard) {
        order = (prevCard.order + nextCard.order) / 2
      } else if (prevCard) {
        order = prevCard.order + 1000
      } else if (nextCard) {
        order = nextCard.order - 1000
      } else {
        order = Date.now()
      }

      // Clear sort only when manually reordering within the same unsorted column
      if (isSameColumn) {
        let clearFn = clearSortFnsRef.current[destination.toColumnId]
        if (clearFn) {
          clearFn()
        }
      }

      submit({
        type: 'set_task_placement',
        task_id: card.id,
        stage_id: destination.toColumnId,
        order,
      })

      if (!isSameColumn && isSorted) {
        setMovedCardId(card.id)
        // clear so card ui gets moved false
        setTimeout(() => setMovedCardId(null), 1000)
      }
    }

    if (isSorted && !isSameColumn) {
      // Cross-column move into a sorted column: apply sort immediately so the
      // card lands in its sorted position
      setBoard(currentBoard => {
        let movedBoard = moveCard(currentBoard, source, destination)
        return {
          ...movedBoard,
          columns: movedBoard.columns.map(col => {
            if (col.id !== destination.toColumnId) return col
            return { ...col, cards: sortCards(col.cards, stageSorts[col.id]) }
          }),
        }
      })
    } else {
      skipSortRef.current = true
      setBoard(currentBoard => moveCard(currentBoard, source, destination))
    }
  }

  let displayBoard = {
    ...board,
    columns: board.columns.map((column, i) => {
      if (column.cards.length === 0) {
        return {
          ...column,
          cards: [
            {
              id: `placeholder-${column.id}`,
              placeholder: true, // this will render the card unable to be dragged
              message:
                i === 0
                  ? `Your tasks will appear here.`
                  : `Move tasks through from start to completion 
with stages.`,
            },
          ],
        }
      }
      return column
    }),
  }

  return (
    <View {...props} onChange={changeFilters}>
      <ControlledBoard
        onCardDragEnd={handleCardMove}
        renderCard={(card, { dragging }) =>
          card.placeholder ? (
            <EmptyCard
              viewPath={`${props.viewPath}/Card(${card.id})`}
              text={card.message}
            />
          ) : (
            <Card
              viewPath={`${props.viewPath}/Card(${card.id})`}
              card={card}
              dragging={dragging}
              moved={card.id === movedCardId}
            />
          )
        }
        renderColumnHeader={column => (
          <StageHeader
            viewPath={`${props.viewPath}/StageHeader(${column.id})`}
            title={column.title}
            count={column.cards.filter(card => !card.placeholder).length}
            stageId={column.id}
            isCurrentUserSubscribed={column.is_current_user_subscribed}
            onSortChange={handleSortChange}
            onRegisterClearSort={handleRegisterClearSort}
          />
        )}
        allowAddCard={false}
        disableColumnDrag
      >
        {displayBoard}
      </ControlledBoard>
    </View>
  )
}
